import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

export type UseWidthBreakpointProps = {
  target?: null | HTMLElement;
};

export type Breakpoint = 'lg' | 'md' | 'sm';

const isMd = () => {
  if (!window) {
    return false;
  }

  return window.matchMedia('(min-width: 768px)').matches;
};

const isLg = () => {
  if (!window) {
    return false;
  }

  return window.matchMedia('(min-width: 1280px)').matches;
};

const getBreakpoint = (): Breakpoint => {
  if (isLg()) {
    return 'lg';
  }

  if (isMd()) {
    return 'md';
  }

  return 'sm';
};

const breakpoints = ['sm', 'md', 'lg'];

const useWidthBreakpoint = (props: UseWidthBreakpointProps = {}) => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(getBreakpoint);

  useEffect(() => {
    const target = props.target ?? document.body;
    const obs = new ResizeObserver(
      debounce(
        () => {
          setBreakpoint(getBreakpoint());
        },
        250,
        { leading: true, trailing: true, maxWait: 1000 },
      ),
    );
    target && obs.observe(target);

    return () => obs.disconnect();
  }, [props.target]);

  const isMatch = useCallback(
    (input: Breakpoint) => {
      const breakpointIndex = breakpoints.indexOf(breakpoint);
      const inputIndex = breakpoints.indexOf(input);

      return inputIndex <= breakpointIndex;
    },
    [breakpoint],
  );

  return { breakpoint, isMatch };
};

export default useWidthBreakpoint;
